import { flushSync } from 'react-dom';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

// Number-list order: idle scheduling, preload recovery, commit acknowledgment (milliseconds). Only
// the preload limit still applies; it is a recovery limit, not a delay on the normal path.
const DEFAULT_TIMEOUTS_MS: [number, number, number] = [1_000, 5_000, 5_000];
// Every commit can restyle the whole document, so subtrees upgrade in groups. Subtrees in the view
// always join a group, and the others fill it up to this size, which keeps its commit short.
const MAX_GROUP = 10;
// A percentage of the window height; one viewport, so the size change happens out of view.
const DEFAULT_ACTIVATION_BUFFER_PCT = 100;
// How long a light table near the view stays hidden while it upgrades, before showing its light rows.
const HIDE_UNTIL_UPGRADED_MS = 1_000;
// Tables whose light rows have shown, which a later scheduler for the same DOM must not hide again.
const lightRowsShown = new WeakSet<Element>();

export type LightSubtreeWait = {
	outcome: 'completed' | 'timedOut' | 'failed' | 'cancelled';
	durationMs: number;
} & (
	| { phase: 'preload'; timeoutMs: number }
	| {
			phase: 'visibleLight';
			activationBufferPx: number;
			viewportObserved: boolean;
			shownInLightMode: boolean;
			visibleAtCommit: boolean;
			viewportToCommitMs?: number;
			wasHidden: boolean;
			interactionProtected: boolean;
	  }
);

type ViewportWait = {
	visible: Set<Element>;
	enteredAt?: number;
	durationMs: number;
	viewportObserved: boolean;
	shownInLightMode: boolean;
	wasHidden: boolean;
	interactionProtected: boolean;
};

type FinishWait = (outcome: LightSubtreeWait['outcome']) => void;

type Interaction = { activeElement: Element | null; selection: Selection | null };

type Subtree = {
	// The serializer creates one LightSubtree boundary per top-level ADF subtree. Set while that
	// boundary is mounted; it re-renders the subtree with Platform's components.
	renderFull?: () => void;
	elements: Set<HTMLElement>;
	visible: Set<Element>;
	// Told to render full, which it then stays.
	upgraded: boolean;
	// Merged rows look unlike the full table, so a light table shows blank space while it upgrades.
	hideUntilUpgraded?: boolean;
	// Elements in the view, from the view observer, so grouping upgrades needs no layout reads.
	inView: Set<Element>;
	viewportObserved: Set<Element>;
	pendingOffscreenUpgrade?: boolean;
	prepare?: () => Promise<void>;
	preparation: 'pending' | 'loading' | 'ready';
	preparationTimer?: number;
	finishPreparationWait?: FinishWait;
	viewportWait?: ViewportWait;
	// 'ready' also follows a failed or timed-out preload, so normal upgrades can recover.
	preloaded: boolean;
};

/**
 * Coordinates light-to-full upgrades for one serialized document snapshot.
 *
 * An IntersectionObserver reports subtrees that come near the view. Once its full components have
 * preloaded, each one upgrades synchronously, unless focus or a selection is inside it. Waiting
 * subtrees upgrade in groups of up to 10: each commit takes every subtree a second observer reports in
 * the view, then fills up with the rest. Groups outside the view are a frame apart, so scrolling can
 * move a subtree ahead. Activated subtrees stay full.
 * A failed or timed-out preload falls back to normal full-component loading.
 *
 * A subtree is near within the configured activation buffer of the view, 1,000px by default.
 * Light tables stay hidden until they upgrade or have been near the view for a second.
 * Stopping cancels observers, listeners, and timers while preserving state for a restart.
 */
export class LightSubtreeScheduler {
	private lightNodeSamples: Array<{ time: number; count: number; offscreenUpgraded: number }> = [];
	private mountedLightNodeCount = 0;
	private offscreenUpgradedNodeCount = 0;

	constructor(private readonly lightNodeCounts?: ReadonlyMap<number, number> | undefined) {}

	/** Use the interaction end time: analytics payload creation can happen after later upgrades. */
	getLightNodeCountAt(time: number): number {
		return this.getNodeMetricsAt(time).count;
	}

	getNodeMetricsAt(time: number): { count: number; offscreenUpgraded: number } {
		let low = 0;
		let high = this.lightNodeSamples.length;
		while (low < high) {
			const middle = Math.floor((low + high) / 2);
			if (this.lightNodeSamples[middle].time <= time) {
				low = middle + 1;
			} else {
				high = middle;
			}
		}
		const sample = low > 0 ? this.lightNodeSamples[low - 1] : undefined;
		return {
			count: sample?.count ?? 0,
			offscreenUpgraded: sample?.offscreenUpgraded ?? 0,
		};
	}

	private recordNodeSample(): void {
		const sample = {
			time: performance.now(),
			count: this.mountedLightNodeCount,
			offscreenUpgraded: this.offscreenUpgradedNodeCount,
		};
		const last = this.lightNodeSamples.length - 1;
		if (this.lightNodeSamples[last]?.time === sample.time) {
			this.lightNodeSamples[last] = sample;
		} else {
			this.lightNodeSamples.push(sample);
		}
	}

	private recordLightNodeChange(id: number, direction: 1 | -1): void {
		const count = this.lightNodeCounts?.get(id);
		if (!count) return;
		this.mountedLightNodeCount += direction * count;
		this.recordNodeSample();
	}

	private subtrees = new Map<number, Subtree>();
	private elementSubtrees = new Map<Element, Subtree>();
	private observer?: IntersectionObserver;
	private viewportObserver?: IntersectionObserver;
	private running = false;
	private readonly preloadTimeoutMs = readPreloadTimeout();
	private activationBufferPx = 0;
	private reportWait?: (wait: LightSubtreeWait) => void;
	private hidden = new Set<HTMLElement>();
	private revealTimers = new Map<Element, number>();
	private upgradeQueue = new Set<Subtree>();
	private upgradeTimer?: number;
	private upgradeFrame?: number;

	private getOrCreateSubtree(id: number): Subtree {
		let subtree = this.subtrees.get(id);
		if (!subtree) {
			subtree = {
				viewportObserved: new Set(),
				elements: new Set(),
				visible: new Set(),
				inView: new Set(),
				upgraded: false,
				preparation: 'ready',
				preloaded: true,
			};
			this.subtrees.set(id, subtree);
		}
		return subtree;
	}

	observe = (id: number, element: HTMLElement, hideUntilUpgraded = false): (() => void) => {
		const subtree = this.getOrCreateSubtree(id);
		subtree.hideUntilUpgraded ||= hideUntilUpgraded;
		if (subtree.elements.size === 0) this.recordLightNodeChange(id, 1);
		subtree.elements.add(element);
		if (subtree.hideUntilUpgraded) {
			this.hide(element);
		}
		this.elementSubtrees.set(element, subtree);
		if (!subtree.upgraded) {
			this.observeElement(element);
			this.observeViewport(subtree, element);
		}
		return () => {
			// React detaches light refs before the full boundary acknowledges its commit.
			this.flushViewportEntries();
			this.viewportObserver?.unobserve(element);
			if (subtree.viewportWait && !subtree.upgraded) {
				this.leaveViewport(subtree, element, performance.now());
			}
			this.observer?.unobserve(element);
			this.reveal(element);
			this.elementSubtrees.delete(element);
			subtree.elements.delete(element);
			subtree.viewportObserved.delete(element);
			if (subtree.elements.size === 0) this.recordLightNodeChange(id, -1);
			subtree.visible.delete(element);
			subtree.inView.delete(element);
		};
	};

	mount(id: number, renderFull: () => void, prepare?: () => Promise<void>): () => void {
		const subtree = this.getOrCreateSubtree(id);
		subtree.renderFull = renderFull;
		// Preserve readiness when React re-registers the effect in this serializer snapshot.
		if (prepare && !subtree.prepare) {
			subtree.prepare = prepare;
			subtree.preparation = 'pending';
			subtree.preloaded = false;
		}
		this.startPreparationTimeout(subtree);
		if (subtree.upgraded) {
			renderFull();
		} else if (subtree.visible.size) {
			// An effect can re-register while its DOM refs and intersection state stay unchanged.
			this.upgradeNear(subtree);
		}
		return () => {
			subtree.renderFull = undefined;
			this.clearPreparationTimer(subtree);
			subtree.finishPreparationWait?.('cancelled');
			subtree.finishPreparationWait = undefined;
			this.finishViewportWait(subtree, 'cancelled');
		};
	}

	/** Start preloading ahead of an upgrade that is about to be forced. */
	prepareNow(id: number): void {
		const subtree = this.subtrees.get(id);
		if (subtree?.preparation === 'pending') {
			this.prepareSubtree(subtree);
		}
	}

	/** Whether the subtree's full components loaded, so an upgrade renders no loading states. */
	isPreloaded(id: number): boolean {
		return this.subtrees.get(id)?.preloaded ?? false;
	}

	/** Whether the selection wholly contains the subtree's DOM, so replacing it moves no boundary. */
	isWhollySelected(id: number, selection: Selection): boolean {
		const elements = this.subtrees.get(id)?.elements;
		return Boolean(
			elements?.size && [...elements].every((element) => selection.containsNode(element, false)),
		);
	}

	/** Activate now, skipping preparation and interaction protection. */
	activateNow(id: number): void {
		this.flushViewportEntries();
		const subtree = this.subtrees.get(id);
		if (subtree) {
			this.activateSubtreeNow(subtree);
		}
	}

	private activateSubtreeNow(subtree: Subtree): void {
		if (!subtree.renderFull || subtree.upgraded) {
			return;
		}
		subtree.upgraded = true;
		// Unknown observer state is not evidence that a subtree is outside the viewport.
		subtree.pendingOffscreenUpgrade = Boolean(
			this.lightNodeCounts &&
			subtree.elements.size > 0 &&
			subtree.viewportObserved.size === subtree.elements.size &&
			!subtree.inView.size,
		);
		subtree.elements.forEach((element) => this.observer?.unobserve(element));
		subtree.renderFull();
	}

	private upgradeNear(subtree: Subtree): void {
		if (!subtree.renderFull || subtree.upgraded || !subtree.visible.size) {
			return;
		}
		if (subtree.preparation === 'pending') {
			this.prepareSubtree(subtree);
			return;
		}
		if (subtree.preparation === 'loading') {
			return;
		}
		this.upgradeQueue.add(subtree);
		this.scheduleUpgrades();
	}

	private scheduleUpgrades(afterFrame = false): void {
		if (
			!this.running ||
			this.upgradeTimer !== undefined ||
			this.upgradeFrame !== undefined ||
			!this.upgradeQueue.size
		) {
			return;
		}
		if (afterFrame) {
			this.upgradeFrame = window.requestAnimationFrame(() => {
				this.upgradeFrame = undefined;
				this.scheduleUpgrades();
			});
		} else {
			this.upgradeTimer = window.setTimeout(this.runUpgrades, 0);
		}
	}

	// One commit for every waiting subtree would keep those in the view blank until the farthest
	// renders, and one commit each would restyle the document per subtree. Each task commits one group,
	// led by those in the view. A group of only those in the view would shrink commits and let the
	// subtrees ahead fall behind the scroll. A zero-delay timer can run before the browser renders
	// again, so after a group outside the view the next waits for a frame, whose geometry shows what
	// scrolled into view.
	private runUpgrades = (): void => {
		this.upgradeTimer = undefined;
		const interaction = readInteraction();
		if (!this.running || !interaction) {
			return;
		}
		// Entries the browser computed but has not delivered yet.
		this.flushViewportEntries();
		// Moving away or interaction protection drops a subtree before grouping, so it takes no place
		// in a group; the observer or a later interaction change queues it again.
		const ready: Subtree[] = [];
		this.upgradeQueue.forEach((subtree) => {
			if (
				subtree.renderFull &&
				!subtree.upgraded &&
				subtree.visible.size &&
				subtree.preparation === 'ready' &&
				!this.hasProtectedInteraction(subtree, interaction)
			) {
				ready.push(subtree);
			} else {
				this.upgradeQueue.delete(subtree);
			}
		});
		const inView = ready.filter((subtree) => subtree.inView.size);
		const outsideView = ready.filter((subtree) => !subtree.inView.size);
		const group = [...inView, ...outsideView.slice(0, Math.max(0, MAX_GROUP - inView.length))];
		group.forEach((subtree) => this.upgradeQueue.delete(subtree));
		if (group.length) {
			flushSync(() => group.forEach((subtree) => this.activateSubtreeNow(subtree)));
		}
		this.scheduleUpgrades(!inView.length);
	};

	acknowledge(id: number): void {
		const subtree = this.subtrees.get(id);
		if (subtree?.renderFull) {
			if (subtree.pendingOffscreenUpgrade) {
				this.offscreenUpgradedNodeCount += this.lightNodeCounts?.get(id) ?? 0;
				this.recordNodeSample();
			}
			subtree.pendingOffscreenUpgrade = false;
			subtree.upgraded = true;
			this.finishViewportWait(subtree, 'completed');
			subtree.elements.forEach((element) => this.viewportObserver?.unobserve(element));
			subtree.inView.clear();
		}
	}

	// Created for the first light element, whose scrolling container clips every other one too.
	private observeElement(element: Element): void {
		if (!this.running) {
			return;
		}
		if (!this.observer) {
			// A margin only grows the root, so the root must be the container that scrolls, or that
			// container would clip it.
			this.observer = this.createNearObserver({
				root: findScrollContainer(element),
				rootMargin: `${this.activationBufferPx}px 0px`,
			});
		}
		this.observer.observe(element);
	}

	private createNearObserver(options?: IntersectionObserverInit): IntersectionObserver {
		const observer: IntersectionObserver = new IntersectionObserver((entries) => {
			// Ignore deliveries from a previous effect lifecycle after a restart.
			if (!this.running || this.observer !== observer) {
				return;
			}
			for (const entry of entries) {
				const subtree = this.elementSubtrees.get(entry.target);
				if (!subtree || subtree.upgraded) {
					continue;
				}
				if (entry.isIntersecting) {
					subtree.visible.add(entry.target);
					this.startReveal(entry.target);
					this.upgradeNear(subtree);
				} else {
					subtree.visible.delete(entry.target);
					this.cancelReveal(entry.target);
				}
			}
		}, options);
		return observer;
	}

	// Hidden from mount, so a fast scroll never paints merged rows before the observer runs.
	private hide(element: HTMLElement): void {
		if (lightRowsShown.has(element)) {
			return;
		}
		element.style.opacity = '0';
		this.hidden.add(element);
	}

	// A blocked or slow upgrade still shows the light rows after a while near the view.
	private startReveal(element: Element): void {
		if (!this.hidden.has(element as HTMLElement) || this.revealTimers.has(element)) {
			return;
		}
		this.revealTimers.set(
			element,
			window.setTimeout(() => {
				lightRowsShown.add(element);
				this.reveal(element);
			}, HIDE_UNTIL_UPGRADED_MS),
		);
	}

	private cancelReveal(element: Element): void {
		window.clearTimeout(this.revealTimers.get(element));
		this.revealTimers.delete(element);
	}

	private reveal(element: Element): void {
		this.cancelReveal(element);
		if (this.hidden.delete(element as HTMLElement)) {
			(element as HTMLElement).style.opacity = '';
		}
	}

	private hasProtectedInteraction(
		subtree: Subtree,
		{ activeElement, selection }: Interaction,
	): boolean {
		for (const element of subtree.elements) {
			if (
				element.contains(activeElement) ||
				(selection && !selection.isCollapsed && selection.containsNode(element, true))
			) {
				if (subtree.viewportWait) subtree.viewportWait.interactionProtected = true;
				return true;
			}
		}
		return false;
	}

	private reportFailure(message: string): void {
		getMonitoringClient().submitError(new Error(message), {
			attribution: Attribution.BACKBONE,
			partialFailure: true,
		});
	}

	private clearPreparationTimer(subtree: Subtree): void {
		window.clearTimeout(subtree.preparationTimer);
		subtree.preparationTimer = undefined;
	}

	private finishPreparation(
		subtree: Subtree,
		outcome: LightSubtreeWait['outcome'] = 'completed',
		failure?: string,
	): void {
		// Ignore late resolution/rejection after a timeout; never activate or report twice.
		if (subtree.preparation !== 'loading') {
			return;
		}
		this.clearPreparationTimer(subtree);
		subtree.preparation = 'ready';
		subtree.preloaded = failure === undefined;
		// Preloading is optional, so a subtree near the view upgrades even after a failed preload.
		this.upgradeNear(subtree);
		subtree.finishPreparationWait?.(outcome);
		subtree.finishPreparationWait = undefined;
		if (failure) {
			this.reportFailure(failure);
		}
	}

	private startPreparationTimeout(subtree: Subtree): void {
		if (
			this.running &&
			subtree.renderFull &&
			subtree.preparation === 'loading' &&
			subtree.preparationTimer === undefined
		) {
			subtree.preparationTimer = window.setTimeout(() => {
				this.finishPreparation(
					subtree,
					'timedOut',
					'Light rendering: subtree preload timed out; falling back to full component loading',
				);
			}, this.preloadTimeoutMs);
		}
	}

	private prepareSubtree(subtree: Subtree): void {
		subtree.preparation = 'loading';
		subtree.finishPreparationWait = this.measurePreload();
		this.startPreparationTimeout(subtree);
		void Promise.resolve()
			.then(subtree.prepare)
			.then(
				() => this.finishPreparation(subtree),
				() =>
					this.finishPreparation(
						subtree,
						'failed',
						'Light rendering: subtree preload failed; falling back to full component loading',
					),
			);
	}

	private observeViewport(subtree: Subtree, element: Element): void {
		if (!this.viewportObserver) return;
		this.viewportObserver.observe(element);
		if (!this.reportWait) return;
		subtree.viewportWait ??= {
			visible: new Set(),
			durationMs: 0,
			viewportObserved: false,
			shownInLightMode: false,
			wasHidden: isDocumentHidden(),
			interactionProtected: false,
		};
	}

	private leaveViewport(subtree: Subtree, element: Element, time: number): void {
		const wait = subtree.viewportWait;
		if (!wait) return;
		wait.visible.delete(element);
		if (!wait.visible.size && wait.enteredAt !== undefined) {
			wait.durationMs += Math.max(0, time - wait.enteredAt);
			wait.enteredAt = undefined;
		}
	}

	private recordViewportEntries = (entries: IntersectionObserverEntry[]): void => {
		for (const entry of entries) {
			const subtree = this.elementSubtrees.get(entry.target);
			if (!subtree) continue;
			subtree.viewportObserved.add(entry.target);
			// Edges count for priority, so a zero-height subtree in the view is in it.
			if (entry.isIntersecting) {
				subtree.inView.add(entry.target);
			} else {
				subtree.inView.delete(entry.target);
			}
			const wait = subtree.viewportWait;
			if (!wait) continue;
			wait.viewportObserved = true;
			wait.wasHidden ||= isDocumentHidden();
			// Edge contact at threshold zero does not mean pixels were visible.
			if (entry.isIntersecting && entry.intersectionRatio > 0) {
				if (!wait.visible.size) wait.enteredAt = entry.time;
				wait.visible.add(entry.target);
				wait.shownInLightMode = true;
			} else {
				this.leaveViewport(subtree, entry.target, entry.time);
			}
		}
	};

	private flushViewportEntries(): void {
		if (this.viewportObserver) this.recordViewportEntries(this.viewportObserver.takeRecords());
	}

	private finishViewportWait(subtree: Subtree, outcome: LightSubtreeWait['outcome']): void {
		if (!subtree.viewportWait) return;
		this.flushViewportEntries();
		const wait = subtree.viewportWait;
		const visibleMs =
			wait.enteredAt === undefined ? 0 : Math.max(0, performance.now() - wait.enteredAt);
		this.reportWait?.({
			phase: 'visibleLight',
			outcome,
			durationMs: wait.durationMs + visibleMs,
			activationBufferPx: this.activationBufferPx,
			viewportObserved: wait.viewportObserved,
			shownInLightMode: wait.shownInLightMode,
			visibleAtCommit: outcome === 'completed' && wait.enteredAt !== undefined,
			viewportToCommitMs:
				outcome === 'completed' && wait.enteredAt !== undefined ? visibleMs : undefined,
			wasHidden: wait.wasHidden || isDocumentHidden(),
			interactionProtected: wait.interactionProtected,
		});
		subtree.viewportWait = undefined;
	}

	private onVisibilityChange = (): void => {
		if (isDocumentHidden()) {
			this.subtrees.forEach((subtree) => {
				if (subtree.viewportWait) subtree.viewportWait.wasHidden = true;
			});
		}
	};

	private measurePreload(): FinishWait | undefined {
		const reportWait = this.reportWait;
		if (!reportWait) return;
		const startedAt = performance.now();
		let finished = false;
		return (outcome) => {
			if (finished) return;
			finished = true;
			reportWait({
				phase: 'preload',
				outcome,
				durationMs: performance.now() - startedAt,
				timeoutMs: this.preloadTimeoutMs,
			});
		};
	}

	// Focus or a selection leaving a subtree lifts its protection.
	private onInteractionChange = (): void => {
		this.subtrees.forEach((subtree) => this.upgradeNear(subtree));
	};

	private onFocusOut = (): void => {
		void Promise.resolve().then(this.onInteractionChange);
	};

	start(reportWait?: (wait: LightSubtreeWait) => void): () => void {
		const documentRef = typeof document === 'undefined' ? undefined : document;
		const windowRef = typeof window === 'undefined' ? undefined : window;
		if (!documentRef || !windowRef) {
			return () => {};
		}
		this.running = true;
		// The metrics experiment controls collection; report every enabled document.
		this.reportWait = reportWait;
		this.activationBufferPx = readActivationBuffer();
		// Orders upgrades, and measures how long light content is visible when metrics are collected.
		const viewportObserver = new IntersectionObserver(
			(entries) => {
				if (this.running && this.viewportObserver === viewportObserver) {
					this.recordViewportEntries(entries);
				}
			},
			{ rootMargin: '0px', threshold: [0, 0.0001] },
		);
		this.viewportObserver = viewportObserver;
		if (this.reportWait) {
			documentRef.addEventListener('visibilitychange', this.onVisibilityChange);
		}
		this.subtrees.forEach((subtree) => this.startPreparationTimeout(subtree));
		this.elementSubtrees.forEach((subtree, element) => {
			if (!subtree.upgraded) {
				// Stopping showed every hidden table; a restart hides them again.
				if (subtree.hideUntilUpgraded) {
					this.hide(element as HTMLElement);
				}
				this.observeElement(element);
				this.observeViewport(subtree, element);
			}
		});
		documentRef.addEventListener('focusout', this.onFocusOut);
		documentRef.addEventListener('selectionchange', this.onInteractionChange);
		return () => {
			this.running = false;
			this.observer?.disconnect();
			this.observer = undefined;
			window.clearTimeout(this.upgradeTimer);
			this.upgradeTimer = undefined;
			if (this.upgradeFrame !== undefined) window.cancelAnimationFrame(this.upgradeFrame);
			this.upgradeFrame = undefined;
			this.upgradeQueue.clear();
			this.hidden.forEach((element) => this.reveal(element));
			this.subtrees.forEach((subtree) => {
				subtree.visible.clear();
				subtree.inView.clear();
				subtree.viewportObserved.clear();
				this.clearPreparationTimer(subtree);
				this.finishViewportWait(subtree, 'cancelled');
				// A resumed preload must not produce an artificially short successful sample.
				subtree.finishPreparationWait?.('cancelled');
				subtree.finishPreparationWait = undefined;
			});
			this.viewportObserver?.disconnect();
			this.viewportObserver = undefined;
			this.reportWait = undefined;
			documentRef.removeEventListener('visibilitychange', this.onVisibilityChange);
			documentRef.removeEventListener('focusout', this.onFocusOut);
			documentRef.removeEventListener('selectionchange', this.onInteractionChange);
		};
	}
}

function isDocumentHidden(): boolean {
	return typeof document !== 'undefined' && document.hidden;
}

function readActivationBuffer(): number {
	const value = StatsigConfigurations.getNumber(
		'cc_light_mode_activation_buffer_pct',
		DEFAULT_ACTIVATION_BUFFER_PCT,
	);
	const pct =
		Number.isFinite(value) && value >= 0 && value <= 300 ? value : DEFAULT_ACTIVATION_BUFFER_PCT;
	return Math.round((window.innerHeight * pct) / 100);
}

// The reader's focus and selection, which an upgrade must not replace.
function readInteraction(): Interaction | undefined {
	const documentRef = typeof document === 'undefined' ? undefined : document;
	const windowRef = typeof window === 'undefined' ? undefined : window;
	if (!documentRef || !windowRef) {
		return undefined;
	}
	return { activeElement: documentRef.activeElement, selection: windowRef.getSelection() };
}

// Read once per scheduler.
function readPreloadTimeout(): number {
	const [, preload = DEFAULT_TIMEOUTS_MS[1]] = StatsigConfigurations.getNumberList(
		'cc_light_mode_rendering_timeouts_ms',
		DEFAULT_TIMEOUTS_MS,
	);
	return preload;
}

// `overflow-x: hidden` alone computes `overflow-y` to `auto`, so check that the container scrolls.
function findScrollContainer(element: Element): Element | null {
	for (let parent = element.parentElement; parent; parent = parent.parentElement) {
		const { overflowY } = getComputedStyle(parent);
		if (
			(overflowY === 'auto' || overflowY === 'scroll') &&
			parent.scrollHeight > parent.clientHeight
		) {
			return parent;
		}
	}
	return null;
}
